import "@andypf/json-viewer";

import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from "@angular/core";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import type { DataResult } from "../../../../model/data.types";
import { GeoTiffRendererService } from "../../../../services/geo-tiff-renderer.service";

/**
 * Origin-agnostic renderer for a normalized DataResult.
 * Renders geojson, json, text, image, geotiff (via canvas + viridis colormap), and binary/unknown types.
 */
@Component({
  selector: "app-content-data-viewer",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [MatIcon, MatProgressSpinner],
  templateUrl: "./content-data-viewer.component.html",
  styleUrl: "./content-data-viewer.component.scss",
})
export class ContentDataViewerComponent {
  data = input.required<DataResult>();

  /** Number of levels to expand in JSON/GeoJSON tree. Default 0 (collapsed). Pass 2 from dialogs. */
  expanded = input<number>(0);

  protected readonly isRenderingTiff = signal(false);

  protected readonly tiffRenderError = signal<string | null>(null);

  private readonly tiffCanvas = viewChild<ElementRef<HTMLCanvasElement>>("tiffCanvas");

  private readonly geoTiffRenderer = inject(GeoTiffRendererService);

  constructor() {
    effect(() => {
      const d = this.data();
      const canvasRef = this.tiffCanvas();
      if (d.type !== "geotiff" || !d.geotiff || !canvasRef) return;
      this.renderTiff(d.geotiff, canvasRef.nativeElement);
    });
  }

  private renderTiff(base64: string, canvas: HTMLCanvasElement): void {
    this.isRenderingTiff.set(true);
    this.tiffRenderError.set(null);
    this.geoTiffRenderer
      .renderToCanvas(base64, canvas)
      .then(() => this.isRenderingTiff.set(false))
      .catch((err) => {
        console.error("ContentDataViewerComponent: GeoTIFF render error", err);
        this.tiffRenderError.set("Failed to render GeoTIFF preview");
        this.isRenderingTiff.set(false);
      });
  }
}
